import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import { makeMatcher } from '../utils/search';
import { shipNoteAPI, salesAPI, shipmentAPI } from '../api';
import toast from 'react-hot-toast';
import { confirmToast } from '../components/ConfirmToast';
import ClientPicker from '../components/ClientPicker';

// ─── Юк хати: распоряжение на отгрузку ───────────────────────────────────────
//
// Между заказом и складом стоял человек с бумажкой: офис писал накладную от
// руки и передавал кладовщику, а тот по ней выдавал. Пока заказов было пять,
// это работало. На сорока кладовщик видит все открытые заказы разом и решает
// сам, какой из них грузить и сколько, — ошибка тут стоит машины, уехавшей не
// к тому клиенту.
//
// Порядок здесь тот же, что у человека в голове: сначала кому, потом по
// какому заказу, потом сколько чего. Заказ не обязателен — возят и то, чего
// никто не заказывал.

const S = {
  card:  { background: 'var(--card)', border: '1px solid var(--border)',
           borderRadius: 12, padding: '14px 16px' },
  head:  { color: 'var(--dim)', fontSize: 11, fontWeight: 700,
           textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 },
  input: { padding: '9px 12px', background: 'var(--input)',
           border: '1px solid var(--border)', borderRadius: 9,
           color: 'var(--text)', fontSize: 14, outline: 'none',
           fontFamily: 'inherit', boxSizing: 'border-box' },
};

const btn = (bg, extra = {}) => ({
  background: bg, border: 'none', borderRadius: 9, color: '#fff',
  padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit', whiteSpace: 'nowrap', ...extra,
});

export default function ShipmentNotes({ preset, onIssued }) {
  const [tab, setTab] = useState('new');
  const canIssue = ['technolog', 'director', 'admin'].includes(
    sessionStorage.getItem('role')) ||
    (sessionStorage.getItem('role') === 'manager' &&
     sessionStorage.getItem('can_ship') === '1');

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[['new', '📝 Выписать'], ['list', '📄 Выписанные']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className="touch-btn" style={{
            padding: '8px 16px', borderRadius: 9, fontSize: 13.5, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
            background: tab === k ? 'var(--accent)' : 'var(--card)',
            border: '1px solid ' + (tab === k ? 'var(--accent)' : 'var(--border)'),
            color: tab === k ? '#fff' : 'var(--muted)',
          }}>{t(l)}</button>
        ))}
      </div>

      {!canIssue && (
        <div style={{ ...S.card, borderColor: '#f59e0b55', background: '#f59e0b10',
                      fontSize: 13.5, color: 'var(--text)', lineHeight: 1.5 }}>
          Выписывает распоряжения главный менеджер. Здесь вы видите отгрузки
          по своим заказам — увезли или ещё ждут склад.
        </div>
      )}

      {tab === 'new'  && canIssue && <IssueForm preset={preset} onIssued={onIssued} />}
      {tab === 'list' && <NotesList />}
    </div>
  );
}

// ─── Выписать ────────────────────────────────────────────────────────────────
function IssueForm({ preset, onIssued }) {
  const [clients, setClients] = useState([]);
  // preset — «разреши и выпиши» одним движением: технолог нажал «Разрешить
  // отгрузку» у заказа, который целиком закрывается складом, и форма
  // открывается на этом заказе с проставленными количествами. Дописать или
  // убрать что-то он всё равно может — но обычно достаточно подтвердить.
  const [clientId, setClientId] = useState(preset ? String(preset.clientId) : '');
  const [orders, setOrders]   = useState([]);
  const [orderId, setOrderId] = useState('');
  const [qty, setQty]         = useState({});      // order_item -> число
  const [extra, setExtra]     = useState([]);      // позиции сверх заказа
  const [carrier, setCarrier] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [note, setNote]       = useState('');
  const [busy, setBusy]       = useState(false);
  const [issued, setIssued]   = useState(null);
  const [products, setProducts] = useState([]);
  // Свободный остаток: то, что лежит, минус обещанное другими открытыми
  // распоряжениями. Считает сервер — на клиенте эту арифметику не собрать.
  const [free, setFree] = useState({});

  // Незакрытые заказы этого клиента. Спрашиваем при смене клиента, а не
  // держим все заказы разом: менеджеру нужен один клиент за раз.
  useEffect(() => {
    setOrderId(''); setQty({}); setExtra([]);
    if (!clientId) { setOrders([]); return; }
    shipNoteAPI.sources(clientId)
      .then(r => {
        const list = r.data.orders || [];
        setOrders(list);
        setFree(r.data.stock || {});
        // Пришли с готовым заказом — выбираем его и сразу заполняем остатком.
        // Ради этого сюда и попали: решение «отдать» уже принято нажатием
        // кнопки, здесь его только оформляют.
        if (preset && preset.orderId) {
          const o = list.find(x => String(x.id) === String(preset.orderId));
          if (o) {
            setOrderId(String(o.id));
            const next = {};
            o.items.forEach(it => { if (it.left > 0) next[it.order_item] = it.left; });
            setQty(next);
          }
        }
      })
      .catch(() => toast.error(t('Заказы не загрузились')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    salesAPI.getClients()
      .then(r => setClients(r.data.results || r.data || []))
      .catch(() => {});
    // Только то, что лежит на складе готовой продукции. Раньше сюда шёл весь
    // справочник — тысяча с лишним позиций, из которых отдать можно две
    // сотни: менеджер выбирал товар, которого физически нет, и узнавал об
    // этом от кладовщика через полчаса.
    shipmentAPI.available()
      .then(r => {
        const by = {};
        (r.data.results || []).forEach(x => {
          const k = x.nomenclature_id;
          if (!by[k]) {
            by[k] = { id: k, name: x.short_name || x.product_name, stock: 0 };
          }
          by[k].stock += x.available || 0;
        });
        setProducts(Object.values(by)
          .filter(x => x.stock > 0)
          .sort((a, b) => a.name.localeCompare(b.name, 'ru')));
      })
      .catch(() => {});
  }, []);

  const client = clients.find(c => String(c.id) === String(clientId)) || null;

  const order = orders.find(o => String(o.id) === String(orderId)) || null;

  // «Отдать всё» — то самое одно движение, ради которого сюда и заходят:
  // чаще всего везут заказ целиком, и проставлять каждую строку руками незачем.
  const takeAll = () => {
    if (!order) return;
    const next = {};
    order.items.forEach(it => { if (it.left > 0) next[it.order_item] = it.left; });
    setQty(next);
  };
  const clearAll = () => setQty({});

  const addExtra = () => setExtra(p => [...p, { product: '', quantity: '' }]);
  const setExtraAt = (i, patch) =>
    setExtra(p => p.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const dropExtra = (i) => setExtra(p => p.filter((_, k) => k !== i));

  const rows = [
    ...(order ? order.items
      .filter(it => Number(qty[it.order_item] || 0) > 0)
      .map(it => ({ order_item: it.order_item, quantity: Number(qty[it.order_item]),
                    label: it.product })) : []),
    ...extra
      .filter(x => x.product && Number(x.quantity || 0) > 0)
      .map(x => ({ product: x.product, quantity: Number(x.quantity),
                   label: (products.find(p => String(p.id) === String(x.product)) || {}).name })),
  ];
  const total = rows.reduce((s, r) => s + r.quantity, 0);

  // Сколько этого продукта свободно и не перебрали ли. «Свободно» — уже с
  // вычетом того, что обещано другими открытыми распоряжениями: между
  // выпиской и отгрузкой проходит день, и товар всё это время лежит на месте.
  const freeOf = (nomId) => {
    const s2 = free[nomId];
    if (s2 !== undefined) return s2;
    const p = products.find(x => String(x.id) === String(nomId));
    return p ? p.stock : 0;
  };

  // Перебор считаем по продукту целиком: одна и та же ЛДСП может стоять и
  // строкой заказа, и добавленной сверх.
  const wanted = {};
  rows.forEach(r => {
    const nid = r.product ? r.product
      : (order && (order.items.find(x => x.order_item === r.order_item) || {}).product_id);
    if (nid) wanted[nid] = (wanted[nid] || 0) + r.quantity;
  });
  const over = (it) => {
    const nid = it.product_id || it.id;
    return (wanted[nid] || 0) > freeOf(nid);
  };
  const overflow = Object.keys(wanted).filter(nid => wanted[nid] > freeOf(nid));

  const issue = async () => {
    if (!client) { toast.error(t('Выберите контрагента')); return; }
    if (!rows.length) { toast.error(t('Не указано ни одной позиции')); return; }
    if (!await confirmToast(
      `Выписать распоряжение: ${client.name}, позиций ${rows.length}, всего ${total} шт?`,
      { confirmText: 'Выписать' })) return;
    setBusy(true);
    try {
      const { data } = await shipNoteAPI.issue({
        client: client.id,
        order: orderId || null,
        items: rows,
        carrier, vehicle, note,
      });
      setIssued(data);
      toast.success(t('Выписано') + ' ' + data.number);
      if (onIssued) onIssued(data);
      // Форму не чистим целиком: тот же клиент часто забирает в несколько
      // приёмов, и заново искать его в списке — лишнее движение.
      setQty({}); setExtra([]); setNote('');
      if (clientId) {
        shipNoteAPI.sources(clientId).then(r => setOrders(r.data.orders || [])).catch(() => {});
      }
    } catch (err) {
      const d = err.response?.data || {};
      if (d.short) toast.error(d.error + ': ' + d.short.join('; '), { duration: 8000 });
      else toast.error(d.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {issued && (
        <div style={{ ...S.card, borderColor: '#22c55e', background: '#22c55e10',
                      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14 }}>
            ✅ {t('Выписано')} <b>{issued.number}</b> · {issued.client} ·{' '}
            {issued.total} {t('шт')}
          </span>
          <button onClick={() => shipNoteAPI.print(issued.id)}
            style={btn('#0ea5e9')}>🖨 {t('Печать накладной')}</button>
          <button onClick={() => setIssued(null)}
            style={{ background: 'none', border: 'none', color: 'var(--dim)',
                     cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
            {t('скрыть')}
          </button>
        </div>
      )}

      {/* 1. Кому */}
      <div style={S.card}>
        <div style={S.head}>1 · {t('Кому везём')}</div>
        <ClientPicker clients={clients} value={clientId} onChange={setClientId} />
      </div>

      {/* 2. По какому заказу */}
      {client && (
        <div style={S.card}>
          <div style={S.head}>2 · {t('По какому заказу')}</div>
          {orders.length === 0 ? (
            <div style={{ color: 'var(--dim)', fontSize: 13.5 }}>
              У этого контрагента нет незакрытых заказов. Можно везти и так —
              добавьте позиции ниже.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 7 }}>
              {orders.map(o => (
                <button key={o.id}
                  onClick={() => { setOrderId(String(o.id)); setQty({}); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                    textAlign: 'left', padding: '9px 12px', borderRadius: 9,
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5,
                    background: String(o.id) === orderId ? 'var(--accent-weak)' : 'var(--bg)',
                    border: '1px solid ' + (String(o.id) === orderId
                                            ? 'var(--accent)' : 'var(--border)'),
                    color: 'var(--text)',
                  }}>
                  <b>{o.order_number}</b>
                  <span style={{ color: 'var(--dim)' }}>{o.status_label}</span>
                  {!o.allowed && (
                    <span style={{ background: '#f59e0b18', color: '#f59e0b',
                                   borderRadius: 6, padding: '1px 8px', fontSize: 11.5,
                                   fontWeight: 700 }}>ждёт разрешения</span>
                  )}
                  <span style={{ marginLeft: 'auto', color: 'var(--muted)' }}>
                    {o.items.length} {t('поз.')} · {t('осталось отдать')}{' '}
                    <b style={{ color: 'var(--text)' }}>
                      {o.items.reduce((s, x) => s + x.left, 0)}
                    </b>
                  </span>
                </button>
              ))}
              {orderId && (
                <button onClick={() => { setOrderId(''); setQty({}); }}
                  style={{ background: 'none', border: 'none', color: 'var(--dim)',
                           cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit',
                           textAlign: 'left', padding: '2px 0' }}>
                  {t('без заказа')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* 3. Сколько чего */}
      {client && (
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                        marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ ...S.head, marginBottom: 0 }}>3 · {t('Что и сколько')}</div>
            {order && (
              <>
                <button onClick={takeAll} style={btn('#22c55e', {
                  padding: '7px 15px', fontSize: 13 })}>
                  ✔ {t('Отдать весь заказ')}
                </button>
                <button onClick={clearAll} style={{
                  background: 'none', border: 'none', color: 'var(--dim)',
                  cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>
                  {t('снять')}
                </button>
              </>
            )}
          </div>

          {order && order.items.map(it => (
            <div key={it.order_item} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0',
              borderBottom: '1px solid var(--border)', fontSize: 13.5,
              opacity: it.left > 0 ? 1 : 0.45,
            }}>
              <span style={{ flex: 1, minWidth: 0, color: 'var(--text)' }}>
                {it.product}
                {it.texture && <span style={{ color: 'var(--dim)' }}> · {it.texture}</span>}
              </span>
              <span style={{ color: 'var(--dim)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                {t('заказано')} {it.quantity} · {t('отдано')} {it.shipped} ·{' '}
                <b style={{ color: it.left > 0 ? '#f59e0b' : 'var(--dim)' }}>
                  {t('осталось')} {it.left}
                </b>
                {' · '}
                {/* Свободно на складе. Стоит рядом с полем ввода: предел
                    нужен там, где ставят число, а не в другом окне. */}
                <b style={{ color: over(it) ? '#ef4444' : 'var(--dim)' }}>
                  {t('на складе')} {freeOf(it.product_id)}
                </b>
              </span>
              <input type="text" inputMode="numeric" disabled={it.left <= 0}
                value={qty[it.order_item] ?? ''}
                onChange={e => {
                  const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                  setQty(p => ({ ...p, [it.order_item]: v }));
                }}
                onFocus={e => e.target.select()}
                placeholder="0"
                style={{ ...S.input, width: 84, textAlign: 'center', fontWeight: 700,
                         borderColor: over(it) ? '#ef4444' : 'var(--border)',
                         color: over(it) ? '#ef4444' : 'var(--text)' }} />
            </div>
          ))}

          {/* Позиции сверх заказа. Довозят остаток прошлой партии, забирают
              то, что просто лежит на складе, — заказа под это нет. */}
          <div style={{ marginTop: 12 }}>
            {extra.map((x, i) => (
              <ExtraRow key={i} row={x} products={products}
                free={x.product ? freeOf(x.product) : null}
                over={x.product ? (wanted[x.product] || 0) > freeOf(x.product) : false}
                onChange={patch => setExtraAt(i, patch)}
                onDrop={() => dropExtra(i)} />
            ))}
            <button onClick={addExtra} style={{
              background: 'none', border: '1px dashed var(--border)', borderRadius: 9,
              color: 'var(--muted)', padding: '8px 16px', fontSize: 13,
              cursor: 'pointer', fontFamily: 'inherit' }}>
              + {t('позиция вне заказа')}
            </button>
          </div>
        </div>
      )}

      {/* 4. Выписать */}
      {client && (
        <div style={S.card}>
          <div style={S.head}>4 · {t('Выписать')}</div>
          <div style={{ display: 'grid', gap: 10, marginBottom: 12,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <input value={carrier} onChange={e => setCarrier(e.target.value)}
              placeholder={t('Через кого (водитель)')} style={S.input} />
            <input value={vehicle} onChange={e => setVehicle(e.target.value)}
              placeholder={t('Машина / вагон')} style={S.input} />
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder={t('Примечание')} style={S.input} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <button onClick={issue} disabled={busy || !rows.length || overflow.length > 0}
              style={btn((rows.length && !overflow.length) ? '#22c55e' : 'var(--bg)', {
                color: (rows.length && !overflow.length) ? '#fff' : 'var(--mid)',
                cursor: (rows.length && !overflow.length) ? 'pointer' : 'not-allowed',
                padding: '12px 28px', fontSize: 15,
              })}>
              {busy ? '…' : `📝 ${t('Выписать распоряжение')}`}
            </button>
            <span style={{ color: 'var(--muted)', fontSize: 13.5 }}>
              {t('позиций')} <b style={{ color: 'var(--text)' }}>{rows.length}</b>
              {' · '}{t('всего')} <b style={{ color: 'var(--text)', fontSize: 17 }}>{total}</b>
            </span>
          </div>
          {overflow.length > 0 && (
            <div style={{ background: '#ef444412', border: '1px solid #ef444455',
                          borderRadius: 9, padding: '10px 13px', fontSize: 13,
                          marginTop: 10, color: 'var(--text)' }}>
              ⚠ По красным строкам на складе столько нет. Свободный остаток уже
              с вычетом того, что обещано другими распоряжениями.
            </div>
          )}
          <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
            Выписка и есть разрешение: склад увидит этот документ и отдаст
            ровно то, что здесь стоит. Заказ при этом разрешается сам —
            отдельной кнопки больше нет.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Строка «вне заказа» ─────────────────────────────────────────────────────
//
// Выпадающий список тут не работает: позиций на складе под две сотни, а
// названия у них длинные и начинаются одинаково — «Плита ДСП ламинированная…».
// Пролистать до нужной глазами нельзя, поэтому поиск по любой части имени, как
// в поле контрагента.
function ExtraRow({ row, products, free, over, onChange, onDrop }) {
  const [q, setQ]       = useState('');
  const [open, setOpen] = useState(false);

  const chosen = products.find(p => String(p.id) === String(row.product)) || null;
  const s2 = q.trim().toLowerCase();
  const shown = (s2
    ? products.filter(p => makeMatcher(s2)(p.name, p.grade_name))
    : products).slice(0, 60);

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 7 }}>
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {chosen ? (
          <div style={{ ...S.input, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                           textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {chosen.name}
            </span>
            <span style={{ fontSize: 12, whiteSpace: 'nowrap',
                           color: over ? '#ef4444' : 'var(--dim)' }}>
              {t('на складе')} {free != null ? free : chosen.stock}
            </span>
            <button onClick={() => { onChange({ product: '' }); setQ(''); }} style={{
              background: 'none', border: 'none', color: 'var(--dim)',
              cursor: 'pointer', fontSize: 15, fontFamily: 'inherit' }}>✕</button>
          </div>
        ) : (
          <input value={q} onFocus={() => setOpen(true)}
            onChange={e => { setQ(e.target.value); setOpen(true); }}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder={t('Найти товар на складе…')}
            style={{ ...S.input, width: '100%' }} />
        )}

        {open && !chosen && (
          <div style={{
            position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 40,
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 9, marginTop: 4, maxHeight: 260, overflowY: 'auto',
            boxShadow: '0 10px 24px rgba(0,0,0,.25)',
          }}>
            {shown.length === 0 && (
              <div style={{ padding: '10px 12px', color: 'var(--dim)', fontSize: 13 }}>
                {t('На складе такого нет')}
              </div>
            )}
            {shown.map(p => (
              <button key={p.id}
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onChange({ product: String(p.id) }); setOpen(false); }}
                style={{
                  display: 'flex', width: '100%', gap: 10, alignItems: 'center',
                  padding: '8px 12px', background: 'none', border: 'none',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13.5, textAlign: 'left',
                  color: 'var(--text)',
                }}>
                <span style={{ flex: 1, minWidth: 0 }}>{p.name}</span>
                <b style={{ color: 'var(--muted)', fontSize: 12.5 }}>{p.stock}</b>
              </button>
            ))}
          </div>
        )}
      </div>

      <input type="text" inputMode="numeric" value={row.quantity}
        onChange={e => onChange({
          quantity: e.target.value.replace(/[^0-9]/g, '').slice(0, 6) })}
        onFocus={e => e.target.select()} placeholder="0"
        style={{ ...S.input, width: 84, textAlign: 'center', fontWeight: 700,
                 borderColor: over ? '#ef4444' : 'var(--border)',
                 color: over ? '#ef4444' : 'var(--text)' }} />
      <button onClick={onDrop} style={{
        background: 'none', border: 'none', color: '#ef4444', padding: '9px 4px',
        cursor: 'pointer', fontSize: 16, fontFamily: 'inherit' }}>✕</button>
    </div>
  );
}

// ─── Выписанные ──────────────────────────────────────────────────────────────
function NotesList() {
  const [rows, setRows]   = useState([]);
  const [only, setOnly]   = useState('issued');

  const load = useCallback(() => {
    shipNoteAPI.list(only ? { status: only } : {})
      .then(r => setRows(r.data.results || []))
      .catch(() => toast.error(t('Не загрузилось')));
  }, [only]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (n) => {
    if (!await confirmToast(`Отменить распоряжение ${n.number}?`,
                            { confirmText: 'Отменить' })) return;
    try {
      await shipNoteAPI.cancel(n.id);
      toast.success(t('Отменено'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    }
  };

  const COLORS = { issued: '#f59e0b', done: '#22c55e', cancelled: 'var(--dim)' };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[['issued', 'Ждут склад'], ['done', 'Отгружены'],
          ['cancelled', 'Отменены'], ['', 'Все']].map(([k, l]) => (
          <button key={k || 'all'} onClick={() => setOnly(k)} className="touch-btn" style={{
            padding: '6px 14px', borderRadius: 18, fontSize: 12.5, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
            background: only === k ? 'var(--accent)' : 'var(--bg)',
            border: '1px solid ' + (only === k ? 'var(--accent)' : 'var(--border)'),
            color: only === k ? '#fff' : 'var(--muted)',
          }}>{t(l)}</button>
        ))}
      </div>

      {rows.length === 0 && (
        <div style={{ ...S.card, color: 'var(--dim)', textAlign: 'center' }}>
          {t('Пусто')}
        </div>
      )}

      {rows.map(n => (
        <div key={n.id} style={{
          ...S.card, borderLeft: '3px solid ' + (COLORS[n.status] || 'var(--border)'),
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '10px 14px',
        }}>
          <span style={{ flex: '1 1 260px', minWidth: 0, fontSize: 13.5 }}>
            <b style={{ color: 'var(--text)' }}>{n.number}</b>
            <span style={{ color: 'var(--text)' }}> · {n.client}</span>
            {n.order_number && (
              <span style={{ color: '#f59e0b' }}> · {n.order_number}</span>
            )}
            <span style={{ display: 'block', color: 'var(--dim)', fontSize: 11.5,
                           marginTop: 2 }}>
              {n.created_by} · {new Date(n.created_at).toLocaleString('ru-RU')}
              {n.done_at && ' · отгружено ' + new Date(n.done_at).toLocaleString('ru-RU')}
            </span>
          </span>
          <span style={{ color: COLORS[n.status], fontSize: 12.5, fontWeight: 700,
                         whiteSpace: 'nowrap' }}>{n.status_label}</span>
          <span style={{ color: 'var(--muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
            {n.total} {t('шт')}
          </span>
          <button onClick={() => shipNoteAPI.print(n.id)}
            style={{ background: 'none', border: '1px solid var(--border)',
                     borderRadius: 8, color: 'var(--muted)', padding: '6px 12px',
                     fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
            🖨 {t('печать')}
          </button>
          {n.status === 'issued' && (
            <button onClick={() => cancel(n)} style={{
              background: 'none', border: '1px solid #ef444455', borderRadius: 8,
              color: '#ef4444', padding: '6px 12px', fontSize: 12.5,
              cursor: 'pointer', fontFamily: 'inherit' }}>
              {t('отменить')}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
